/**
 * Feature Flags Configuration
 *
 * Controls which features are visible to users.
 * Use this to deploy code safely while hiding unfinished features.
 *
 * Feature states:
 * - enabled: true = visible to everyone
 * - enabled: false = hidden unless user is in betaUsers list
 * - betaUsers: array of email addresses who can see beta features
 */

// Users who can see beta/unreleased features
const BETA_USERS = [
  'mark.herman@3dcolor.com',
  'amanda.billiter@3dcolor.com',
];

// Feature configuration
const FEATURE_CONFIG = {
  // ============================================
  // RELEASED FEATURES (visible to everyone)
  // ============================================
  PROJECT_SCHEDULE: {
    enabled: true,
    description: 'Project schedule view with milestones',
  },
  PRINTER_DASHBOARD: {
    enabled: true,
    description: 'PA Dashboard showing printer status',
  },
  TEAM_VIEW: {
    enabled: true,
    description: 'Team view for PM users',
  },
  TIME_REPORTS: {
    enabled: true,
    description: 'Time tracking reports',
  },
  MY_PINNED_QUOTES: {
    enabled: true,
    description: 'Pin favorite quotes for quick access',
  },
  NEW_NAVIGATION: {
    enabled: true,
    description: 'New section-based navigation shell with light theme',
  },

  // ============================================
  // PRODUCTION VIEWS (work center specific)
  // Set ready: true when view is ready for that work center
  // ============================================
  PA_QUEUE: {
    enabled: false,
    betaUsers: BETA_USERS,
    description: 'PA Queue - Production Art work queue',
    // Future: workCenters: ['PA'] - show to PA users when ready
  },
  ASSEMBLY_QUEUE: {
    enabled: false,
    betaUsers: BETA_USERS,
    description: 'Assembly Queue - Assembly work queue',
    // Future: workCenters: ['ASSEMBLY', 'OPTI'] - show to Assembly users when ready
  },
  SHIPPING_QUEUE: {
    enabled: false,
    betaUsers: BETA_USERS,
    description: 'Shipping Queue - Shipping work queue',
    // Future: workCenters: ['SHIPPING'] - show to Shipping users when ready
  },
  PAINT_MODEL_VIEW: {
    enabled: false,
    betaUsers: BETA_USERS,
    description: 'Paint & Model View - 3D production work queue',
    // Future: workCenters: ['PAINT', '3D MODEL'] - show to Paint/Model users when ready
  },

  // ============================================
  // BETA FEATURES (visible to beta users only)
  // ============================================
  PM_COMMAND_CENTER: {
    enabled: false,
    betaUsers: ['mark.herman@3dcolor.com'], // Mark only for now
    description: 'PM Command Center for project management',
  },
  PM_CENTER_PROTOTYPES: {
    enabled: false,
    betaUsers: BETA_USERS,
    description: 'Show experimental PM Center options C and D',
  },
  WORK_ORDER_REVIEW: {
    enabled: false,
    betaUsers: BETA_USERS,
    description: 'AI-powered work order review and merge',
  },
  PRODUCTION_SCHEDULE: {
    enabled: false,
    betaUsers: BETA_USERS,
    description: 'Kanban-style production schedule board',
  },
  PRODUCTION_FLOOR_VIEW: {
    enabled: false,
    betaUsers: BETA_USERS,
    description: 'Simplified production floor interface for workers',
  },
  ADMIN_USER_MANAGEMENT: {
    enabled: false,
    betaUsers: BETA_USERS,
    description: 'Admin interface for managing user accounts',
  },

  // ============================================
  // SCHEDULING VIEWS
  // ============================================
  SCHEDULING_BOARD: {
    enabled: false,
    betaUsers: BETA_USERS,
    description: 'Kanban scheduling board',
  },
  SCHEDULING_CAPACITY: {
    enabled: false,
    betaUsers: BETA_USERS,
    description: 'Capacity planning view',
  },
  SCHEDULING_BACKLOG: {
    enabled: false,
    betaUsers: BETA_USERS,
    description: 'Backlog management view',
  },
  SCHEDULING_RISKS: {
    enabled: false,
    betaUsers: BETA_USERS,
    description: 'Risk tracking view',
  },
  SCHEDULING_CHANGES: {
    enabled: false,
    betaUsers: BETA_USERS,
    description: 'Change audit trail view',
  },

  // ============================================
  // REVENUE DASHBOARD ENHANCEMENTS
  // ============================================
  PENDING_QUOTES_PAGE: {
    enabled: false,
    betaUsers: BETA_USERS,
    description: 'Pending Quotes Management page for pipeline hygiene',
  },
  JOBBOSS_WRITE_BACK: {
    enabled: false,
    betaUsers: BETA_USERS,
    description: 'Enable write operations to JobBOSS (ship dates, archive)',
  },
  DEFINITIONS_TOOLTIPS: {
    enabled: false,
    betaUsers: BETA_USERS,
    description: 'Contextual definition tooltips on KPI tiles and columns',
  },
  DEFINITIONS_PAGE: {
    enabled: false,
    betaUsers: BETA_USERS,
    description: 'Data Definitions library page',
  },
  HOWTO_GUIDE: {
    enabled: false,
    betaUsers: BETA_USERS,
    description: 'How-To Guide access for Revenue Dashboard',
  },
};

/**
 * Check if a feature is enabled for a given user
 * @param {string} feature - Feature key from FEATURE_CONFIG
 * @param {string} userEmail - User's email address
 * @returns {boolean} - Whether the feature should be visible
 */
export function isFeatureEnabled(feature, userEmail) {
  const config = FEATURE_CONFIG[feature];

  // Unknown feature = default to enabled (fail-open for existing features)
  if (!config) {
    console.warn(`Unknown feature flag: ${feature}`);
    return true;
  }

  // Feature is globally enabled
  if (config.enabled) {
    return true;
  }

  // Check if user is in beta list
  if (config.betaUsers && userEmail) {
    const normalizedEmail = userEmail.toLowerCase().trim();
    if (config.betaUsers.some(email => email.toLowerCase() === normalizedEmail)) {
      return true;
    }
  }

  return false;
}

/**
 * Get all features and their status for a user (useful for debugging)
 * @param {string} userEmail - User's email address
 * @returns {Object} - Map of feature names to their enabled status
 */
export function getFeatureStatus(userEmail) {
  const status = {};
  for (const [feature, config] of Object.entries(FEATURE_CONFIG)) {
    status[feature] = {
      enabled: isFeatureEnabled(feature, userEmail),
      description: config.description,
      isBeta: !config.enabled,
    };
  }
  return status;
}

// Export feature keys as constants for type safety
export const FEATURES = {
  PROJECT_SCHEDULE: 'PROJECT_SCHEDULE',
  PRINTER_DASHBOARD: 'PRINTER_DASHBOARD',
  TEAM_VIEW: 'TEAM_VIEW',
  TIME_REPORTS: 'TIME_REPORTS',
  MY_PINNED_QUOTES: 'MY_PINNED_QUOTES',
  PM_COMMAND_CENTER: 'PM_COMMAND_CENTER',
  PM_CENTER_PROTOTYPES: 'PM_CENTER_PROTOTYPES',
  WORK_ORDER_REVIEW: 'WORK_ORDER_REVIEW',
  PRODUCTION_SCHEDULE: 'PRODUCTION_SCHEDULE',
  PRODUCTION_FLOOR_VIEW: 'PRODUCTION_FLOOR_VIEW',
  ADMIN_USER_MANAGEMENT: 'ADMIN_USER_MANAGEMENT',
  NEW_NAVIGATION: 'NEW_NAVIGATION',
  // Production views
  PA_QUEUE: 'PA_QUEUE',
  ASSEMBLY_QUEUE: 'ASSEMBLY_QUEUE',
  SHIPPING_QUEUE: 'SHIPPING_QUEUE',
  PAINT_MODEL_VIEW: 'PAINT_MODEL_VIEW',
  // Scheduling views
  SCHEDULING_BOARD: 'SCHEDULING_BOARD',
  SCHEDULING_CAPACITY: 'SCHEDULING_CAPACITY',
  SCHEDULING_BACKLOG: 'SCHEDULING_BACKLOG',
  SCHEDULING_RISKS: 'SCHEDULING_RISKS',
  SCHEDULING_CHANGES: 'SCHEDULING_CHANGES',
  // Revenue Dashboard enhancements
  PENDING_QUOTES_PAGE: 'PENDING_QUOTES_PAGE',
  JOBBOSS_WRITE_BACK: 'JOBBOSS_WRITE_BACK',
  DEFINITIONS_TOOLTIPS: 'DEFINITIONS_TOOLTIPS',
  DEFINITIONS_PAGE: 'DEFINITIONS_PAGE',
  HOWTO_GUIDE: 'HOWTO_GUIDE',
};

export default FEATURE_CONFIG;
